import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PwaInstallInlinePanel } from '../shared/PwaInstallInlinePanel';
import { prefersReducedMotion } from '../../utils/subscriptionAccessibility';

export const LEARNER_SETTINGS_IN_APP_ALERTS_ID = 'learner-settings-in-app-alerts';

const INSTALL_OFFER_ANIM_MS = 300;

interface InAppAlertsFieldProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  showInstallOffer: boolean;
  focused?: boolean;
  checkboxClassName: string;
  optionHoverClassName: string;
  /** True while the install panel is open or still collapsing, so the parent layout does not snap. */
  onOfferOccupyingChange?: (occupying: boolean) => void;
}

export const InAppAlertsField: React.FC<InAppAlertsFieldProps> = ({
  checked,
  onCheckedChange,
  showInstallOffer,
  focused = false,
  checkboxClassName,
  optionHoverClassName,
  onOfferOccupyingChange,
}) => {
  const { t } = useTranslation('settings');
  const [offerRendered, setOfferRendered] = useState(showInstallOffer);

  useEffect(() => {
    if (showInstallOffer) {
      setOfferRendered(true);
      return undefined;
    }
    const delay = prefersReducedMotion() ? 0 : INSTALL_OFFER_ANIM_MS;
    const timer = window.setTimeout(() => setOfferRendered(false), delay);
    return () => window.clearTimeout(timer);
  }, [showInstallOffer]);

  useEffect(() => {
    onOfferOccupyingChange?.(showInstallOffer || offerRendered);
  }, [showInstallOffer, offerRendered, onOfferOccupyingChange]);

  return (
    <div
      id={LEARNER_SETTINGS_IN_APP_ALERTS_ID}
      data-tour-id="quick-settings-notifications-alerts"
      tabIndex={-1}
      className={`rounded-xl outline-none transition-[box-shadow,background-color] duration-500 ${
        focused
          ? 'ring-2 ring-amber-400/90 ring-offset-2 ring-offset-white dark:ring-amber-400/70 dark:ring-offset-gray-900'
          : ''
      }`}
    >
      <label
        className={`flex cursor-pointer items-start justify-between gap-3 rounded-xl border border-slate-200/90 bg-white/95 px-4 py-3.5 shadow-sm transition-colors dark:border-gray-600 dark:bg-gray-900/60 ${optionHoverClassName}`}
      >
        <div className="min-w-0 pe-2 text-start">
          <span className="block text-sm font-semibold text-slate-800 dark:text-slate-100">{t('inAppAlerts')}</span>
          <span className="mt-1 block text-xs leading-snug text-slate-500 dark:text-slate-400">
            {t('inAppAlertsHint', {
              defaultValue:
                'Phone notifications when you get a new message, plus in-app badges. First install the app, then allow notifications inside it.',
            })}
          </span>
        </div>
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onCheckedChange(e.target.checked)}
          className={`mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 ${checkboxClassName}`}
        />
      </label>
      <div
        className="grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none"
        style={{
          gridTemplateRows: showInstallOffer ? '1fr' : '0fr',
          opacity: showInstallOffer ? 1 : 0,
        }}
      >
        <div className="min-h-0 overflow-hidden">
          {offerRendered && (
            <div
              className={`transition-[transform,opacity] duration-200 ease-out motion-reduce:transition-none ${
                showInstallOffer ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-2 opacity-0'
              }`}
              aria-hidden={!showInstallOffer}
            >
              <PwaInstallInlinePanel className="mt-2" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
